import { memo, useEffect } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router'

import { useModuleConfig } from '@webapp/app/config/useModuleConfig'
import {
  makeSelectApplicationStatus,
  makeSelectFeatureFlags,
  makeSelectFeatureSwitches,
  makeSelectIsAppReady,
  makeSelectNavigationPermissions,
  makeSelectRole,
  makeSelectSetupProgress,
} from '@webapp/core/stores/global'
import { useIsSuperuser, useProcurifyIntl } from '@webapp/hooks'
import { useAppRoutes } from '@webapp/hooks/useAppRoutes'
import { makeSelectNotificationCount } from '@webapp/stores/notificationCount/selectors'
import { useInjectSaga } from '@webapp/utils'
import { NavigationLoader } from './components/NavigationLoader'
import { Navigation } from './Navigation'
import { getBaseNavigation } from './NavigationMapping'
import { NavigationHeaders, NavigationSections } from './types'
import {
  filterByApplicationStatus,
  filterByCondition,
  filterByPermissions,
  getAnalyticsSection,
  getExpenseSection,
  getPurchaseSection,
  getSpendSection,
} from './utils'
import { fetchCounts } from '../../stores/notificationCount'
import saga from '../../stores/notificationCount/sagas'

export const API_NOTIFICATION_COUNT = '/api/v3/users/me/notification-count/'

export const ConnectedNavigation = memo(() => {
  const intl = useProcurifyIntl()
  const { url } = useAppRoutes()

  useInjectSaga({ key: 'ConnectedNavigation', saga })

  const dispatch = useDispatch()
  const { pathname, hash } = useLocation()

  const navigationPermissions = useSelector(makeSelectNavigationPermissions())
  const featureSwitches = useSelector(makeSelectFeatureSwitches())
  const featureFlags = useSelector(makeSelectFeatureFlags())
  const notificationCount = useSelector(makeSelectNotificationCount())
  const applicationStatus = useSelector(makeSelectApplicationStatus())
  const setupProgress = useSelector(makeSelectSetupProgress())
  const role = useSelector(makeSelectRole())
  const isAppReady = useSelector(makeSelectIsAppReady())
  const isSuperuser = useIsSuperuser()

  useEffect(() => {
    dispatch(fetchCounts())
  }, [])

  const { navigation: navigationEntitlement } = useModuleConfig()

  const baseNavigation = isAppReady ? getBaseNavigation(intl, url) : []

  const conditionFilteredNavigation = baseNavigation
    .filter(filterByApplicationStatus(applicationStatus, role))
    .filter(
      filterByCondition({
        featureFlags: { ...featureFlags, ...featureSwitches },
        setupProgress,
        entitlement: navigationEntitlement,
        isSuperuser,
      })
    )

  const filteredNavigation = conditionFilteredNavigation.filter(
    filterByPermissions(navigationPermissions)
  )

  const navigationItems = {
    [NavigationSections.HOME]: filteredNavigation.find(
      (navItem) => navItem.id === NavigationSections.HOME
    ),
    [NavigationSections.APPROVE]: filteredNavigation.find(
      (navItem) => navItem.id === NavigationSections.APPROVE
    ),
    [NavigationSections.EXPENSE]: getExpenseSection(filteredNavigation),
    [NavigationSections.PURCHASE]: getPurchaseSection(filteredNavigation),
    [NavigationSections.SPEND]: getSpendSection(filteredNavigation),
    [NavigationSections.ANALYTICS]: getAnalyticsSection(filteredNavigation),
    [NavigationSections.SETTINGS]: filteredNavigation.find(
      (navItem) => navItem.id === NavigationSections.SETTINGS
    ),
    /**
     * Resolved before `filterByPermissions`: that filter drops any item whose
     * `id` is missing from the backend-supplied navigation permissions, and
     * `discover_more` is a front-end-only surface the backend never emits.
     * Visibility is fully covered by the item's own condition (superuser +
     * feature flag).
     */
    [NavigationSections.DISCOVER_MORE]: conditionFilteredNavigation.find(
      (navItem) => navItem.id === NavigationHeaders.DISCOVER_MORE
    ),
  }

  return isAppReady ? (
    <Navigation
      location={pathname + hash}
      notificationCount={notificationCount}
      navigationItems={navigationItems}
    />
  ) : (
    <NavigationLoader />
  )
})
